import { libraryRootNode } from "@/components/suggestions/utils/libraryItem";
import { UnitNodeModel } from "@/models/nodes/UnitNodeModel";
import { isPowerOperator } from "@/models/utils/semanticChecks";

import type { Suggestion, SuggestionItem } from "@/components/suggestions/types";
import type { CursorModel } from "@/models/CursorModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

const ALLOWED_OPERATOR_TYPES = new Set(["power", "fraction"]);
const ALLOWED_SYMBOLS = new Set(["^", "/"]);

function isOperatorItem(item: SuggestionItem): item is SuggestionItem<"operator"> {
  return item.type === "operator";
}

function isInsidePowerWithinUnit(cursorNode: NodeModel | undefined): boolean {
  if (!cursorNode) return false;

  for (const ancestor of cursorNode.ancestors) {
    if (isPowerOperator(ancestor)) {
      return true;
    }
    if (ancestor.is(UnitNodeModel)) {
      return false;
    }
  }
  return false;
}

export function isCharAllowedInUnit(key: string, cursorNode: NodeModel | undefined): boolean {
  if (key.length !== 1) return false;
  if (/^\p{L}$/u.test(key)) return true;
  if (ALLOWED_SYMBOLS.has(key)) return true;
  if (/^\p{N}$/u.test(key)) return isInsidePowerWithinUnit(cursorNode);
  return false;
}

export function isAllowedInUnit(
  suggestion: Suggestion,
  cursorNode: NodeModel | undefined,
): boolean {
  if (suggestion.type !== "single") return false;

  const { item } = suggestion;

  const root = libraryRootNode(item);
  if (root) return isNodeAllowedInUnit(root, cursorNode);

  if (item.type === "plainText" || item.type === "symbol") return true;

  if (isOperatorItem(item)) {
    const operatorType = item.props?.operatorType;
    return operatorType !== undefined && ALLOWED_OPERATOR_TYPES.has(operatorType);
  }

  if (item.type === "operatorSymbol") {
    return ALLOWED_SYMBOLS.has(item.symbol);
  }

  if (item.type === "number") {
    return isInsidePowerWithinUnit(cursorNode);
  }

  return false;
}

/**
 * The same rules for a library item's node, read off the node JSON: the operator type on an
 * operator, the glyph of an operator symbol's symbol child.
 */
function isNodeAllowedInUnit(node: NodeJson, cursorNode: NodeModel | undefined): boolean {
  switch (node.type) {
    case "plainText":
    case "symbol":
      return true;
    case "operator":
      return ALLOWED_OPERATOR_TYPES.has(String(node.props.operatorType));
    case "operatorSymbol": {
      const symbol = node.children.find((child) => child.type === "symbol");
      return ALLOWED_SYMBOLS.has(String(symbol?.props.value));
    }
    case "number":
      return isInsidePowerWithinUnit(cursorNode);
    default:
      return false;
  }
}

/**
 * When the cursor sits inside a unit node but the input being inserted is not allowed in a unit,
 * move the cursor past the unit's owner so the new node lands outside the unit.
 */
export function escapeUnitForDisallowedInput(cursor: CursorModel, isAllowed: boolean): void {
  const { doc } = cursor;
  const cursorContext = doc.cursorContext(doc.cursorContextId);
  const isBounded = !!cursorContext?.boundaryInlineNodeIds?.length;
  if (isBounded) return;

  const unitAncestor = cursor.node?.ancestors.find((n) => n.is(UnitNodeModel));
  if (unitAncestor && !isAllowed) {
    const unitOwner = unitAncestor.parent;
    if (unitOwner) {
      cursor.moveAfterNode(unitOwner);
    }
  }
}
